import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { db } from "../../firebase";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import {
    Calendar,
    Clock,
    Search,
    Filter,
    PlusCircle
} from "lucide-react";

// Traducciones para los textos
const translations = {
    en: {
        blogTitle: "Blog",
        blogTagline: "Insights and news about technology and innovation",
        allArticles: "All Articles",
        searchPlaceholder: "Search articles...",
        filterByCategory: "Filter by Category",
        allCategories: "All Categories",
        readTime: "min read",
        by: "By",
        noArticles: "No articles found",
        noArticlesDescription: "Try changing your search or filter criteria",
        loading: "Loading articles...",
        createArticle: "Create Article",
        categories: [
            "Technology",
            "AI",
            "Cloud Computing",
            "3D Printing",
            "Business",
            "Innovation",
            "Development",
            "Case Studies",
        ]
    },
    es: {
        blogTitle: "Blog",
        blogTagline: "Perspectivas y noticias sobre tecnología e innovación",
        allArticles: "Todos los Artículos",
        searchPlaceholder: "Buscar artículos...",
        filterByCategory: "Filtrar por Categoría",
        allCategories: "Todas las Categorías",
        readTime: "min de lectura",
        by: "Por",
        noArticles: "No se encontraron artículos",
        noArticlesDescription: "Intenta cambiar tu búsqueda o criterios de filtro",
        loading: "Cargando artículos...",
        createArticle: "Crear Artículo",
        categories: [
            "Tecnología",
            "IA",
            "Computación en la Nube",
            "Impresión 3D",
            "Negocios",
            "Innovación",
            "Desarrollo",
            "Casos de Estudio",
        ]
    }
};

// Colores del logo
const logoColors = {
    blue: "#0088D1",    // Azul superior
    teal: "#33B8B8",    // Verde-azulado derecha
    green: "#6CC24A",   // Verde inferior
    orange: "#F08234",  // Naranja izquierda
    darkBlue: "#0F3246" // Centro azul oscuro
};

// Categorías con colores específicos
const categoryColors = {
    "Technology": logoColors.blue,
    "AI": logoColors.teal,
    "Cloud Computing": logoColors.green,
    "3D Printing": logoColors.orange,
    "Business": logoColors.darkBlue,
    "Innovation": logoColors.blue,
    "Development": logoColors.teal,
    "Case Studies": logoColors.green,

    // Versiones en español
    "Tecnología": logoColors.blue,
    "IA": logoColors.teal,
    "Computación en la Nube": logoColors.green,
    "Impresión 3D": logoColors.orange,
    "Negocios": logoColors.darkBlue,
    "Innovación": logoColors.blue,
    "Desarrollo": logoColors.teal,
    "Casos de Estudio": logoColors.green
};

// Obtener color para categoría
const getCategoryColor = (category) => {
    return categoryColors[category] || logoColors.teal;
};

// Componente de tarjeta de artículo
const ArticleCard = ({ article, t, onClick }) => {
    // Formatear fecha
    const formatDate = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return new Intl.DateTimeFormat(t === translations.es ? "es-ES" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(date);
    };

    // Calcular tiempo de lectura
    const calculateReadTime = (content) => {
        if (!content) return 1;
        const textOnly = content.replace(/<[^>]*>/g, "");
        const words = textOnly.split(/\s+/).length;
        return Math.max(1, Math.ceil(words / 200));
    };

    return (
        <div
            className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full transition-transform hover:-translate-y-1 cursor-pointer"
            onClick={onClick}
        >
            {article.coverImage && (
                <div className="h-48 overflow-hidden">
                    <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                    />
                </div>
            )}
            <div className="p-5 flex-grow flex flex-col">
                {article.category && (
                    <div
                        className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-2 text-white"
                        style={{ backgroundColor: getCategoryColor(article.category) }}
                    >
                        {article.category}
                    </div>
                )}
                <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
                    {article.title}
                </h3>
                {article.summary && (
                    <p className="text-gray-600 mb-4 line-clamp-3 flex-grow">
                        {article.summary}
                    </p>
                )}
                <div className="flex justify-between items-center text-sm text-gray-500 mt-auto pt-4 border-t">
                    <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(article.createdAt)}
                    </div>
                    <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {calculateReadTime(article.content)} {t.readTime}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente principal de listado de blogs
const BlogList = () => {
    const navigate = useNavigate();
    const { user, currentLanguage } = useUserAuth();
    const t = translations[currentLanguage || "en"];

    const [articles, setArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");

    // Cargar artículos
    useEffect(() => {
        const loadArticles = async () => {
            try {
                setLoading(true);

                // Consultar artículos publicados
                const blogsRef = collection(db, "blogs");
                const q = query(
                    blogsRef,
                    where("published", "==", true),
                    orderBy("createdAt", "desc")
                );

                const querySnapshot = await getDocs(q);
                const articlesData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setArticles(articlesData);
                setFilteredArticles(articlesData);
            } catch (error) {
                console.error("Error loading articles:", error);
            } finally {
                setLoading(false);
            }
        };

        loadArticles();
    }, []);

    // Filtrar artículos cuando cambia la búsqueda o categoría
    useEffect(() => {
        let filtered = [...articles];

        // Filtrar por búsqueda
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(article =>
                article.title.toLowerCase().includes(query) ||
                (article.summary && article.summary.toLowerCase().includes(query)) ||
                (article.content && article.content.toLowerCase().includes(query)) ||
                (article.tags && article.tags.some(tag => tag.toLowerCase().includes(query)))
            );
        }

        // Filtrar por categoría
        if (selectedCategory) {
            filtered = filtered.filter(article => article.category === selectedCategory);
        }

        setFilteredArticles(filtered);
    }, [searchQuery, selectedCategory, articles]);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category === t.allCategories ? "" : category);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleArticleClick = (slug) => {
        navigate(`/blog/${slug}`);
    };

    const handleCreateArticle = () => {
        navigate("/createblog");
    };

    // Mostrar pantalla de carga
    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen py-12 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                        style={{ borderColor: logoColors.blue, borderTopColor: 'transparent' }}></div>
                    <p className="text-gray-600">{t.loading}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen pt-6 pb-12">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Barra de búsqueda y filtros simplificada */}
                <div className="mb-8">
                    <div className="flex flex-col gap-5">
                        {/* Búsqueda */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                placeholder={t.searchPlaceholder}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md text-sm leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Menú desplegable de categorías horizontal */}
                        <div className="relative overflow-x-auto">
                            <div className="flex space-x-2 pb-1" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                                <button
                                    onClick={() => setSelectedCategory("")}
                                    className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${selectedCategory === ""
                                            ? "bg-gray-800 text-white shadow-md"
                                            : "bg-white text-gray-700 hover:bg-gray-100"
                                        }`}
                                >
                                    {t.allCategories}
                                </button>
                                {t.categories.map((category, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedCategory(category)}
                                        className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${selectedCategory === category
                                                ? "text-white shadow-md"
                                                : "bg-white text-gray-700 hover:bg-gray-100"
                                            }`}
                                        style={{
                                            backgroundColor: selectedCategory === category
                                                ? getCategoryColor(category)
                                                : ""
                                        }}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </div>

                            {/* Indicadores de scroll */}
                            <div className="absolute right-0 top-0 bottom-0 bg-gradient-to-l from-gray-50 to-transparent w-12 pointer-events-none"></div>
                        </div>

                        {/* Botón de crear (si el usuario está autenticado) */}
                        {user && (
                            <div className="flex justify-end">
                                <button
                                    onClick={handleCreateArticle}
                                    className="flex items-center justify-center px-4 py-2 text-white text-sm rounded-md transition-colors shadow-sm hover:shadow"
                                    style={{ backgroundColor: logoColors.blue }}
                                >
                                    <PlusCircle className="w-4 h-4 mr-2" />
                                    {t.createArticle}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Listado de artículos */}
                <section>

                    {filteredArticles.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredArticles.map((article) => (
                                <ArticleCard
                                    key={article.id}
                                    article={article}
                                    t={t}
                                    onClick={() => handleArticleClick(article.slug)}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white rounded-lg shadow">
                            <Search className="h-12 w-12 mx-auto mb-4" style={{ color: logoColors.teal }} />
                            <h3 className="text-xl font-medium text-gray-800 mb-2">{t.noArticles}</h3>
                            <p className="text-gray-600">{t.noArticlesDescription}</p>
                        </div>
                    )}
                </section>
            </div>

            {/* Estilos CSS */}
            <style jsx="true">{`
                .line-clamp-2 {
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                }
                
                .line-clamp-3 {
                  display: -webkit-box;
                  -webkit-line-clamp: 3;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                }
            `}</style>
        </div>
    );
};

export default BlogList;